import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";

import type {
  ProfileLinkConfig,
  SkillTag,
  SkillTagSchema,
} from "../../../../../client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faEdit } from "@fortawesome/free-regular-svg-icons";
import { DynamicInput } from "../../../../../components/input/DynamicInput";
import { EditorInput } from "../../../../../components/input/EditorInput";
import {
  BulletSectionTitle,
  Checkbox,
  isString,
  Popover,
  PopoverContent,
  PopoverTrigger,
  trim,
} from "../../../../../components";
import { ProjectSettingsContainer } from "./ProjectSettingsContainer";
import { cn } from "../../../../../components/utils/cn";
import { useUpdateProfileLinkMotivation } from "../../../../../services/organtization/update-profile-link-motivation";
import EditorSkillTagButton from "../../../../../components/atoms/EditorSkillTagButton";

import {
  faCircleExclamation,
  faCirclePlus,
  faMagicWandSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { LeadRowCard } from "../card/LeadRowCard";
import { useTheme, useThemeColors } from "../../../../../contexts";

interface Props {
  linkConfigData: ProfileLinkConfig;
  assignmentPitch: string;
  setAssignmentPitch: (value: string) => void;
  isGeneratingAssigmentPitch: boolean;
  handleUpdateLink: (data: Partial<ProfileLinkConfig>) => void;
  onShowAddSkillsDialog: (skillTags: SkillTag[]) => void;
  assignmentInfoAdded: boolean;
  organizationId: string;
  leadId: string;
  linkId: string;
  skillsAddedFromDialog: SkillTag[];
  onAddedCustomSkills: () => void;
  backendIsGeneratingProfileSummary: boolean;
  isDefaultLead: boolean;
}

export const LinkCreatorProfileSummary = ({
  linkConfigData,
  assignmentPitch,
  setAssignmentPitch,
  isGeneratingAssigmentPitch,
  onShowAddSkillsDialog,
  handleUpdateLink,
  assignmentInfoAdded,
  organizationId,
  leadId,
  linkId,
  skillsAddedFromDialog,
  onAddedCustomSkills,
  backendIsGeneratingProfileSummary,
  isDefaultLead,
}: Props) => {
  const [localAssignmentPitch, setLocalAssignmentPitch] =
    useState(assignmentPitch);
  const [openPopoverIndex, setOpenPopoverIndex] = useState<number | null>(null);
  const processedSkillsRef = useRef<string[]>([]);

  const { secondary } = useThemeColors();

  useEffect(() => {
    setLocalAssignmentPitch(assignmentPitch);
  }, [assignmentPitch]);

  const updateProfileAssignmentMotivation = useUpdateProfileLinkMotivation({
    onMutate: () => {
      // Handle loading state if needed
    },
    onSuccess: (config) => {
      if (config.assignmentPitch) {
        handleUpdateLink({ assignmentPitch: config.assignmentPitch });
        setAssignmentPitch(config.assignmentPitch);
      }
    },
    onSettled() {
      // Handle settled state if needed
    },
  });

  // Handle skills addition directly when skillsAddedFromDialog changes
  if (skillsAddedFromDialog.length > 0) {
    const skillNames = skillsAddedFromDialog.map((skill) => skill.name);
    const alreadyProcessed = skillNames.every((name) =>
      processedSkillsRef.current.includes(name),
    );

    if (!alreadyProcessed) {
      let newTopSkills: SkillTag[] = [
        ...linkConfigData.profileSummaryConfig?.topSkills!,
      ];
      skillsAddedFromDialog.forEach((skill) => {
        newTopSkills.push(skill);
      });

      if (
        linkConfigData.profileSummaryConfig?.topSkills!.length !==
        newTopSkills.length
      ) {
        processedSkillsRef.current = skillNames;
        handleUpdateLink({
          profileSummaryConfig: {
            ...linkConfigData.profileSummaryConfig!,
            topSkills: newTopSkills,
          },
        });
        onAddedCustomSkills();
      }
    }
  }

  const renderProfileSummary = () => {
    return (
      <div className="w-full">
        <div className="w-full">
          {linkConfigData.profileSummaryConfig?.showIntroText && (
            <>
              <DynamicInput
                className="w-full"
                inputClassName="text-xs p-0"
                placeholder="Add a custom motivation on why this profile is a good fit for this assignment"
                rows={0}
                value={localAssignmentPitch}
                onChange={(e) => {
                  setLocalAssignmentPitch(e.target.value);
                }}
                onFinishWriting={(value: string) => {
                  handleUpdateLink({ assignmentPitch: value });
                }}
                disabled={isDefaultLead}
              />
            </>
          )}

          {linkConfigData.profileSummaryConfig?.showIntroText &&
            (linkConfigData.profileSummaryConfig.showHighlights ||
              linkConfigData.profileSummaryConfig.showTopSkills) && (
              <div className="bg-gray-3 w-full h-[1px] my-4" />
            )}

          {linkConfigData.profileSummaryConfig?.showHighlights &&
            linkConfigData.highlights &&
            linkConfigData.highlights.map((highlight, index) => (
              <div
                key={index}
                className={cn(
                  "flex items-center gap-2",
                  index === 0 ? "mt-0" : "mt-2",
                )}
              >
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  size="sm"
                  color="#797C83"
                />
                <EditorInput
                  placeholder="Add a highlight or leave empty to remove"
                  value={highlight}
                  transformValue={trim}
                  validate={isString}
                  inputClassName="text-xs text-black"
                  onFinishWriting={(newHighlight) => {
                    const newHighlights = linkConfigData.highlights
                      ? [...linkConfigData.highlights]
                      : [];
                    newHighlights[index] = newHighlight;
                    handleUpdateLink({ highlights: newHighlights });
                  }}
                  editable={!isDefaultLead}
                  disabled={isDefaultLead}
                />
              </div>
            ))}
        </div>

        {linkConfigData.profileSummaryConfig?.showTopSkills &&
          linkConfigData.profileSummaryConfig.showHighlights && (
            <div className="bg-gray-3 w-full h-[1px] my-4" />
          )}
        {!backendIsGeneratingProfileSummary &&
        linkConfigData.profileSummaryConfig?.showTopSkills ? (
          <>
            <div className="flex flex-row flex-wrap items-center justify-start w-full gap-2">
              <div
                className="cursor-pointer flex flex-row justify-center items-center text-sm gap-1 transform transition-transform hover:scale-110 mr-1 group/add-skill"
                onClick={() => {
                  if (isDefaultLead) {
                    return;
                  }
                  onShowAddSkillsDialog(
                    linkConfigData.profileSummaryConfig!.topSkills!,
                  );
                }}
              >
                <FontAwesomeIcon
                  icon={faCirclePlus}
                  className="w-4 h-4 group-hover/add-skill:scale-125 transition-transform duration-200"
                />
                <span className="text-xs">Add skill</span>
              </div>
              {linkConfigData.profileSummaryConfig!.topSkills!.map(
                (skillTag, index) => {
                  return (
                    <Popover
                      key={index}
                      open={openPopoverIndex === index}
                      onOpenChange={(open) => {
                        setOpenPopoverIndex(open ? index : null);
                      }}
                    >
                      <PopoverTrigger disabled={isDefaultLead} asChild>
                        <button
                          className="inline-block cursor-pointer"
                          onClick={() => {
                            if (isDefaultLead) {
                              return;
                            }
                            setOpenPopoverIndex(
                              openPopoverIndex === index ? null : index,
                            );
                          }}
                        >
                          <EditorSkillTagButton
                            profileSkillTag={skillTag}
                            text={skillTag.name}
                            disableEditing={true}
                            handleClick={() => {}}
                            keepLeftPadding={true}
                            isStatic={true}
                          />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="z-[9999] pointer-events-auto">
                        <div className="flex flex-col pointer-events-auto">
                          <button
                            className="flex items-center gap-2 rounded-lg px-4 py-2 hover:bg-gray-100 pointer-events-auto"
                            onClick={() => {
                              // Handle remove skill logic here
                              setOpenPopoverIndex(null);
                              handleUpdateLink({
                                profileSummaryConfig: {
                                  ...linkConfigData.profileSummaryConfig!,
                                  topSkills:
                                    linkConfigData.profileSummaryConfig!.topSkills!.filter(
                                      (skill) => skill.name !== skillTag.name,
                                    ),
                                },
                              });
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <FontAwesomeIcon
                              width={14}
                              height={14}
                              icon={faEdit}
                              className="shrink-0"
                            />
                            <span
                              className="text-sm text-black"
                              style={{
                                pointerEvents: "auto",
                                cursor: "pointer",
                              }}
                            >
                              Remove
                            </span>
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                },
              )}
            </div>
          </>
        ) : null}
        {!isDefaultLead && (
          <LeadRowCard
            className={cn(
              "text-xs mt-3 w-full",
              assignmentInfoAdded
                ? "bg-green-1 border-green-2"
                : "bg-red-1 border-red-2",
            )}
            type="dashed"
          >
            <div className="flex flex-1 items-center justify-start gap-3">
              <FontAwesomeIcon
                width={14}
                height={14}
                icon={assignmentInfoAdded ? faCircleCheck : faCircleExclamation}
                className="w-4 h-4 shrink-0"
              />

              {assignmentInfoAdded ? (
                <p>
                  The summary highlights skills and experiences relevant for the
                  assignment information
                </p>
              ) : (
                <p>
                  Add assignment info to optimize summary to fit specific lead
                </p>
              )}
            </div>
          </LeadRowCard>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div style={{ "--text-color": secondary } as React.CSSProperties}>
        <BulletSectionTitle childrenClassName="text-[var(--text-color)]">
          Profile Summary
        </BulletSectionTitle>
      </div>
      <div className="flex flex-row items-start gap-4">
        <Checkbox
          checked={
            linkConfigData.profileSummaryConfig
              ? linkConfigData.profileSummaryConfig.showProfileSummary &&
                !linkConfigData.profileMissingInformation
              : false
          }
          disabled={linkConfigData.profileMissingInformation ?? false}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            if (linkConfigData.assignmentPitch === "" && checked) {
              updateProfileAssignmentMotivation.mutate({
                path: {
                  organizationID: organizationId,
                  leadID: leadId,
                  linkID: linkId,
                },
                body: {
                  includeName: false,
                },
              });
            }
            handleUpdateLink({
              profileSummaryConfig: {
                ...linkConfigData.profileSummaryConfig,
                showProfileSummary: checked,
              },
            });
          }}
        />
        <ProjectSettingsContainer
          className={cn(
            "flex flex-col items-center grow p-4 rounded-2xl",
            linkConfigData.profileSummaryConfig?.boxColor !== ""
              ? "bg-gray-1"
              : "",
          )}
          enabled={
            (linkConfigData?.profileSummaryConfig?.showProfileSummary &&
              !linkConfigData.profileMissingInformation) ||
            false
          }
        >
          {isGeneratingAssigmentPitch || backendIsGeneratingProfileSummary ? (
            <p className="text-sm mt-2">Generating profile summary...</p>
          ) : linkConfigData.profileMissingInformation ? (
            <p className="text-xs mt-2">
              The profile does not contain enough information to generate a
              summary
            </p>
          ) : (
            renderProfileSummary()
          )}
        </ProjectSettingsContainer>
      </div>
    </div>
  );
};
